import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowDown, Check } from "lucide-react";
import { IMAGES, waLink, STATS } from "../data/content";

const LINES = ["Solusi Hukum untuk", "Bisnis & Kehidupan Anda"];

const lineVariant = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 1.05, ease: [0.22, 1, 0.36, 1], delay: 0.5 + i * 0.16 },
  }),
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "10%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);

  return (
    <section
      id="beranda"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen flex flex-col justify-center overflow-hidden bg-navy-dark pt-28 pb-16"
    >
      {/* Parallax image */}
      <motion.div style={{ scale: imgScale, y: imgY }} className="absolute inset-0 z-0">
        <img src={IMAGES.hero} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-navy-dark/85 via-navy/80 to-navy-dark/95" />
        <div className="absolute inset-0 noise opacity-[0.12] mix-blend-overlay" />
      </motion.div>

      <motion.div style={{ y: textY }} className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 w-full">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="flex items-center gap-3 mb-8"
        >
          <span className="h-px w-12 bg-gold" />
          <span className="text-sm uppercase tracking-[0.3em] text-gold-light font-semibold">
            FYLegal.ID — For Your Legal Needs
          </span>
        </motion.div>

        <h1 className="font-serif text-white leading-[0.95] tracking-tight text-[3.1rem] sm:text-7xl lg:text-[6.2rem] font-medium">
          {LINES.map((line, i) => (
            <span key={i} className="block overflow-hidden pb-1">
              <motion.span
                custom={i}
                variants={lineVariant}
                initial="hidden"
                animate="visible"
                className="block"
              >
                {i === 1 ? (
                  <>
                    Bisnis & <span className="italic text-gold">Kehidupan</span> Anda
                  </>
                ) : (
                  line
                )}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.1 }}
          className="mt-8 max-w-2xl text-white/70 text-base md:text-lg leading-relaxed"
        >
          FYLegal.ID menyediakan layanan hukum yang profesional bagi individu maupun perusahaan —
          mulai dari legalitas usaha, perlindungan kekayaan intelektual, penyusunan kontrak,
          pendampingan hukum perusahaan, penyelesaian sengketa, hingga konsultasi hukum.{" "}
          <span className="text-white">
            Kami berkomitmen memberikan solusi yang transparan, efektif, dan berorientasi pada
            kepastian hukum.
          </span>
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.25 }}
          className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4"
        >
          <a
            href={waLink()}
            target="_blank"
            rel="noreferrer"
            data-testid="hero-cta-consult"
            className="group inline-flex items-center gap-2 rounded-full bg-gold text-navy px-8 py-4 text-base font-semibold hover:bg-gold-light transition-all duration-300 shadow-gold"
          >
            Konsultasi Sekarang
            <ArrowUpRight className="h-5 w-5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
          <a
            href="#layanan"
            data-testid="hero-cta-services"
            className="inline-flex items-center gap-2 rounded-full border border-white/30 text-white px-8 py-4 text-base font-medium hover:bg-white hover:text-navy transition-all duration-300"
          >
            Lihat Layanan
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.4 }}
          data-testid="hero-trust"
          className="mt-9 flex flex-wrap items-center gap-x-7 gap-y-3"
        >
          {["Pendampingan Hukum Profesional", "Solusi Hukum Terpercaya", "Melayani Klien di Seluruh Indonesia"].map((t) => (
            <div key={t} className="flex items-center gap-2.5 text-sm text-white/80">
              <Check className="h-4 w-4 text-gold shrink-0" strokeWidth={2.5} />
              <span>{t}</span>
            </div>
          ))}
        </motion.div>
      </motion.div>

      {/* Stats overlapping bottom */}
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, delay: 1.4 }}
        className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 w-full mt-16 lg:mt-24"
        data-testid="hero-stats"
      >
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px rounded-3xl overflow-hidden bg-white/10 border border-white/10 backdrop-blur-md">
          {STATS.map((s) => (
            <div key={s.label} className="bg-navy/40 px-6 py-7 text-center">
              <div className="font-serif text-4xl md:text-5xl text-gold font-semibold">{s.value}</div>
              <div className="mt-1 text-xs md:text-sm text-white/70 tracking-wide">{s.label}</div>
            </div>
          ))}
        </div>
      </motion.div>

      <motion.a
        href="#tentang"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 hidden lg:flex flex-col items-center gap-2 text-white/50"
      >
        <span className="text-xs uppercase tracking-[0.2em]">Scroll</span>
        <ArrowDown className="h-4 w-4 animate-bounce" />
      </motion.a>
    </section>
  );
};
